import { Card, EmptyState, formatValue, KpiCard, LoadingBlock, percentChange, UnavailableKpiCard, type FormatOptions } from '@/design'
import { KPI_KEYS, KPI_SPARKLINE_SOURCES, type Kpi, type KpiUnavailableReason } from '@/api/types'

import { useDrillDown, useKpiSparklineSources, useKpis, useMoney } from './hooks'

/** What a not-available tile says it is waiting for. */
const UNAVAILABLE_NOTES: Record<KpiUnavailableReason, string> = {
  needs_browser_events: 'Needs consented browser events',
  needs_ad_platform: 'Needs a connected ad platform',
  no_read_model: 'No data source yet',
}

/** A KPI the source cannot give yet: "Not live yet" and what it is waiting for. */
function UnavailableKpi({ kpi, reason }: { kpi: Kpi; reason: KpiUnavailableReason }) {
  return <UnavailableKpiCard className="ov-kpi-unavailable" label={kpi.label} value="Not live yet" note={UNAVAILABLE_NOTES[reason]} />
}

function KpiTile({
  kpi,
  compareLabel,
  money,
  href,
  sparkline,
}: {
  kpi: Kpi
  compareLabel: string | null
  money: FormatOptions
  href: string
  sparkline: readonly number[]
}) {
  if (kpi.unavailable) return <UnavailableKpi kpi={kpi} reason={kpi.unavailable} />
  const value = kpi.value
  // The source's own change when it computed one (null: none to show); otherwise derived here.
  const change = value === null ? null : kpi.change !== undefined ? kpi.change : percentChange(value, kpi.previous)
  return (
    <KpiCard
      label={kpi.label}
      value={value === null ? '—' : formatValue(value, kpi.format, money)}
      change={change}
      polarity={kpi.polarity}
      comparisonLabel={compareLabel}
      sparkline={sparkline.length > 0 ? sparkline : undefined}
      href={href}
      note={kpi.comparisonNote ?? (value !== null && kpi.previous === null && compareLabel ? 'Live count' : undefined)}
      detail={kpi.secondary ? `${kpi.secondary.label} ${formatValue(kpi.secondary.value, kpi.format, money)}` : undefined}
    />
  )
}

/**
 * The thirteen headline metrics, each a link to the page that explains it. A tile's
 * sparkline is the KPI's own, or the daily points of the route `KPI_SPARKLINE_SOURCES`
 * names for it (the Sign-ups tile's), read beside the KPIs: such a tile shows no
 * sparkline until they arrive, or if they cannot be read.
 */
export function KpiGrid() {
  const kpis = useKpis()
  const sources = useKpiSparklineSources()
  const sparklineOf = (kpi: Kpi) => {
    const source = KPI_SPARKLINE_SOURCES[kpi.key]
    return source ? sources[source] : kpi.sparkline
  }
  const href = useDrillDown()
  const money = useMoney(kpis.data?.meta.currency)

  if (kpis.isError) {
    return (
      <Card>
        <EmptyState variant="outage" compact description="Headline metrics could not be loaded." />
      </Card>
    )
  }

  const compareLabel = kpis.data?.meta.period?.compareLabel ?? null

  return (
    <section aria-labelledby="ov-kpis-heading">
      <h2 id="ov-kpis-heading" className="ci-visually-hidden">
        Headline metrics
      </h2>
      <ul className="ov-kpis" aria-busy={kpis.isFetching || undefined}>
        {kpis.data
          ? kpis.data.data.map((kpi) => (
              <li key={kpi.key} data-kpi={kpi.key}>
                <KpiTile
                  kpi={kpi}
                  compareLabel={compareLabel}
                  money={money}
                  href={href(kpi.drillDown)}
                  sparkline={sparklineOf(kpi)}
                />
              </li>
            ))
          : KPI_KEYS.map((key) => (
              <li key={key}>
                <div className="ci-kpi">
                  <LoadingBlock rows={2} />
                </div>
              </li>
            ))}
      </ul>
    </section>
  )
}
